import { getFormSlugDisplayLabel } from "@/features/diaries/utils/displayLabels";
import { formatScheduleSummary } from "@patient-watch/forms";
import { DEFAULT_LOCALE, localizeSchedulePreviewSummary, type SupportedLocale } from "@patient-watch/i18n";
import type { Timing } from "@patient-watch/types";

export interface SchedulePreviewTiming {
    interval?: number | null;
    time_unit?: string | null;
    repeats?: number | null;
    is_infinite?: boolean | null;
    is_autoname?: boolean | null;
    label?: string | null;
}

/** Columns needed to build a schedule preview. Never select `schedule_name` for display. */
export const schedulePreviewTimingSelect = "interval, time_unit, repeats, is_infinite, is_autoname, label";

export const schedulePreviewEmbed = `schedule_timings(timings(${schedulePreviewTimingSelect}))`;

/**
 * Compact schedule identity from timings, e.g. `Baseline, 3m, 6m, 1y`.
 * Does not fall back to `schedule_name`.
 */
export function schedulePreviewLabel(
    timings: SchedulePreviewTiming[] | null | undefined,
    locale: SupportedLocale = DEFAULT_LOCALE,
    fallback = "Schedule"
): string {
    const usable = (timings ?? []).filter(
        (timing): timing is SchedulePreviewTiming & { interval: number; time_unit: string } =>
            typeof timing?.interval === "number" && typeof timing.time_unit === "string" && timing.time_unit.length > 0
    );
    if (usable.length === 0) return fallback;

    const summary = formatScheduleSummary({
        timings: usable.map((timing) => ({
            interval: timing.interval,
            time_unit: timing.time_unit,
            repeats: timing.repeats ?? 0,
            is_infinite: Boolean(timing.is_infinite),
            is_autoname: timing.is_autoname !== false,
            label: timing.label ?? null,
        })) as Timing["Row"][],
    });
    const localized = localizeSchedulePreviewSummary(summary, locale).trim();
    return localized || fallback;
}

type ScheduleTimingEmbed = {
    timings?: SchedulePreviewTiming | SchedulePreviewTiming[] | null;
};

/** Read timings from a `schedules(schedule_timings(timings(...)))` embed or an already-flattened `timings` array. */
export function timingsFromScheduleEmbed(
    schedule:
        | {
              timings?: SchedulePreviewTiming[] | null;
              schedule_timings?: ScheduleTimingEmbed[] | null;
          }
        | null
        | undefined
): SchedulePreviewTiming[] {
    if (schedule?.timings?.length) return schedule.timings;
    return (schedule?.schedule_timings ?? []).flatMap((link) => {
        const timing = link.timings;
        if (!timing) return [];
        return Array.isArray(timing) ? timing : [timing];
    });
}

export function scheduleEmbedLabel(
    schedule: Parameters<typeof timingsFromScheduleEmbed>[0],
    locale: SupportedLocale = DEFAULT_LOCALE,
    fallback = "Schedule"
): string {
    return schedulePreviewLabel(timingsFromScheduleEmbed(schedule), locale, fallback);
}

/**
 * Stored form name, exactly as written (`Cat-PROM5`, `FAOS - Foot and Ankle Outcome Score`).
 * Does not title-case the slug.
 */
export function formNameLabel(
    form: { form_name?: string | null; slug?: string | null } | null | undefined,
    fallback = "Form"
): string {
    const formName = form?.form_name?.trim();
    return formName || fallback;
}

/**
 * Short form identity for breadcrumbs and list titles.
 * English uses the slug (`back-and-leg-pain` → `Back And Leg Pain`). Other locales keep the translated form name.
 */
export function formBreadcrumbLabel(
    form: { slug?: string | null; form_name?: string | null } | null | undefined,
    locale: SupportedLocale = DEFAULT_LOCALE,
    fallback = "Form"
): string {
    const formName = form?.form_name?.trim();
    if (locale !== DEFAULT_LOCALE && formName) return formName;
    return getFormSlugDisplayLabel(form, fallback);
}
